const urlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi;

// Emoji detection
const isEmojiOnly = (text: string): boolean => {
    const trimmed = text.trim();
    if (trimmed.length === 0) return false;

    const emojiRegex = /^(?:[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]|[\u{1F700}-\u{1F77F}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{2B50}]|[\u{23E9}-\u{23F3}]|[\u{23F8}-\u{23FA}]|[\u{200D}]|[\u{FE0F}]|[\u{20E3}]|[\u{E0020}-\u{E007F}]|[\u{1F3FB}-\u{1F3FF}]|\s)+$/u;

    return emojiRegex.test(trimmed);
};

export interface TextSegment {
    text: string;
    bold: boolean;
    italic: boolean;
    isUrl: boolean;
    emojiOnly: boolean;
}

export const styleSplitter = (text: string): TextSegment[] => {
    // First, find all URLs and their positions
    const urlMatches: { start: number; end: number; url: string }[] = [];
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
        urlMatches.push({ start: match.index, end: match.index + match[0].length, url: match[0] });
    }

    const doubleStarIndices: number[] = [];
    const singleStarIndices: number[] = [];

    const doubleUnderlineIndices: number[] = [];
    const singleUnderlineIndices: number[] = [];

    // Check if position is inside a URL
    const isInsideUrl = (pos: number) => urlMatches.some(m => pos >= m.start && pos < m.end);

    for (let i = 0; i < text.length; i++) {
        if (!isInsideUrl(i)) {
            if (text[i] === '*') {
                if (text[i + 1] === '*' && !isInsideUrl(i + 1)) {
                    doubleStarIndices.push(i);
                    i += 1;
                } else {
                    singleStarIndices.push(i);
                }
            }

            if (text[i] === '_') {
                if (text[i + 1] === '_' && !isInsideUrl(i + 1)) {
                    doubleUnderlineIndices.push(i);
                    i += 1;
                } else {
                    singleUnderlineIndices.push(i);
                }
            }
        }
    }

    // If unclosed, add virtual closing marker at end of text to extend style
    if (doubleStarIndices.length % 2 === 1) doubleStarIndices.push(text.length);
    if (doubleUnderlineIndices.length % 2 === 1) doubleUnderlineIndices.push(text.length);
    if (singleStarIndices.length % 2 === 1) singleStarIndices.push(text.length);
    if (singleUnderlineIndices.length % 2 === 1) singleUnderlineIndices.push(text.length);

    const chunks: TextSegment[] = [];

    const starIndices = doubleStarIndices.length > 0 ? doubleStarIndices : singleStarIndices;
    const underlineIndices = doubleUnderlineIndices.length > 0 ? doubleUnderlineIndices : singleUnderlineIndices;

    const isDoubleStar = doubleStarIndices.length > 0;
    const isDoubleUnderline = doubleUnderlineIndices.length > 0;

    let chunk = "";
    let bold = false;
    let italic = false;

    for (let idx = 0; idx < text.length; idx++) {
        const currentUrlMatch = urlMatches.find(m => idx === m.start);

        if (currentUrlMatch) {
            // Save any pending chunk
            if (chunk.length > 0) {
                chunks.push({ text: chunk, bold, italic, isUrl: false, emojiOnly: !bold && !italic && isEmojiOnly(chunk) });
                chunk = "";
            }
            // Add URL chunk
            chunks.push({ text: currentUrlMatch.url, bold: false, italic: false, isUrl: true, emojiOnly: false });
            idx = currentUrlMatch.end - 1; // -1 because loop will increment
        } else if (starIndices[0] === idx) {
            if (chunk.length > 0) {
                chunks.push({ text: chunk, bold, italic, isUrl: false, emojiOnly: !bold && !italic && isEmojiOnly(chunk) });
                chunk = "";
            }
            bold = !bold;
            starIndices.shift();

            if (isDoubleStar) idx += 1;
        } else if (underlineIndices[0] === idx) {
            if (chunk.length > 0) {
                chunks.push({ text: chunk, bold, italic, isUrl: false, emojiOnly: !bold && !italic && isEmojiOnly(chunk) });
                chunk = "";
            }
            italic = !italic;
            underlineIndices.shift();

            if (isDoubleUnderline) idx += 1;
        } else {
            chunk += text[idx];
        }
    }

    if (chunk.length > 0) {
        chunks.push({ text: chunk, bold, italic, isUrl: false, emojiOnly: !bold && !italic && isEmojiOnly(chunk) });
    }

    return chunks.length > 0 ? chunks : [{ text, bold: false, italic: false, isUrl: false, emojiOnly: isEmojiOnly(text) }];
};

export const dateFormatter = (timestamp: number): string => {
    const now = Date.now() / 1000;

    if (Math.abs(now - timestamp) < 60 * 3) {
        return "Just now";
    }

    const date = new Date(timestamp * 1000);

    if (Math.abs(now - timestamp) < 60 * 60 * 24) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    if (Math.abs(now - timestamp) < 60 * 60 * 24 * 30) {
        return date.toLocaleDateString([], { month: '2-digit', day: '2-digit' });
    }

    return date.toLocaleDateString([], { year: 'numeric', month: '2-digit', day: '2-digit' });
};

// Bubble data types
export interface BubbleData {
    messageid: string;
    type: "text" | "emoji" | "images" | "card";
    text?: string;
    segments?: TextSegment[];
    images?: string[];
    isLastBubble?: boolean;
    timestamp?: number;
    showSenderInfo?: boolean;
    role?: "user" | "assistant";
}

export interface BubbleRenderData extends BubbleData {
    id: string;
    senderid?: string;
    senderName?: string;
    senderAvatar?: string;
}

export interface SenderInfo {
    name?: string;
    avatar?: string;
}

// Process a single ChatMessage into multiple bubbles
export const processMessageToBubbles = (
    message: { messageid: string; content: string; addressid: string; images?: string[]; created_at: number },
    previousMessage?: { messageid: string; content: string; addressid: string; images?: string[]; created_at: number },
    senderInfo?: SenderInfo,
    role: "user" | "assistant" = "assistant"
): BubbleRenderData[] => {
    const bubbles: BubbleRenderData[] = [];
    const { messageid, content, addressid, images, created_at } = message;

    // Extract embedded images from markdown
    const markdownImages = Array.from(content.matchAll(/!\[(.*?)\]\((.*?)\)/g))
        .filter(match => match[1] !== 'Voice')
        .map(match => match[2]);

    // Remove ALL embedded content from text
    const contentWithoutImages = content.replace(/!\[.*?\]\(.*?\)/g, '').trim();

    // Check if we should show sender info (first message from this sender)
    const showSenderInfo = !previousMessage || previousMessage.addressid !== addressid;

    // Common bubble properties
    const baseBubbleProps = {
        messageid,
        role,
        timestamp: created_at,
        senderid: addressid,
        senderName: senderInfo?.name,
        senderAvatar: senderInfo?.avatar,
    };

    // Split content by double newlines
    const textBlocks = contentWithoutImages.split('\n\n').filter(text => text.trim().length > 0);

    let bubbleCnt = 0;

    // Create bubble for attached images if any
    const hasAttachedImages = images && images.length > 0;
    if (hasAttachedImages) {
        const isLastBubble = textBlocks.length === 0 && markdownImages.length === 0;

        bubbles.push({
            ...baseBubbleProps,
            id: `${messageid}-${bubbleCnt}`,
            type: "images",
            images,
            isLastBubble,
            showSenderInfo,
        });
        bubbleCnt += 1;
    }

    // Create bubble for markdown images if any
    if (markdownImages.length > 0) {
        const isLastBubble = textBlocks.length === 0;

        bubbles.push({
            ...baseBubbleProps,
            id: `${messageid}-${bubbleCnt}`,
            type: "images",
            images: markdownImages,
            isLastBubble,
            showSenderInfo: bubbleCnt === 0 && showSenderInfo,
        });
        bubbleCnt += 1;
    }

    // Create bubbles for text blocks
    textBlocks.forEach((text, index) => {
        const isFirstBubble = bubbleCnt === 0;
        const isLastBubble = index === textBlocks.length - 1;

        // Determine bubble type
        const type: BubbleData["type"] = isEmojiOnly(text) ? "emoji" : "text";

        bubbles.push({
            ...baseBubbleProps,
            id: `${messageid}-${bubbleCnt}`,
            type,
            text,
            segments: styleSplitter(text),
            isLastBubble,
            showSenderInfo: isFirstBubble && showSenderInfo,
        });
        bubbleCnt += 1;
    });

    return bubbles;
};
